import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Car } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { tAuto } from "@/lib/tAuto";

/**
 * Ouve `push_notifications` do user actual e mostra um modal global sempre
 * que chega uma confirmação Pay4All (GPO/GPR). Funciona em qualquer rota.
 */
export default function GlobalPaymentConfirmedGate() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [taxiOpen, setTaxiOpen] = useState(false);
  const [payload, setPayload] = useState<{ booking_id?: string; property_title?: string } | null>(null);
  const [taxiLink, setTaxiLink] = useState<string | null>(null);
  const [taxiDeepLink, setTaxiDeepLink] = useState<string | null>(null);

  // Carrega configurações de táxi uma vez
  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("platform_settings")
        .select("key, value")
        .in("key", ["taxi_link", "taxi_deep_link", "partners"]);
      if (!data) return;
      for (const row of data as any[]) {
        if (row.key === "taxi_link") setTaxiLink((row.value?.url || row.value) as string);
        if (row.key === "taxi_deep_link") setTaxiDeepLink((row.value?.url || row.value) as string);
        if (row.key === "partners" && row.value?.taxi) {
          setTaxiLink((prev) => prev || row.value.taxi.link || null);
          setTaxiDeepLink((prev) => prev || row.value.taxi.deep_link || null);
        }
      }
    })();
  }, []);

  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel(`global-payment-confirmed-${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "push_notifications",
          filter: `user_id=eq.${user.id}`,
        },
        (evt) => {
          const row = evt.new as any;
          if (row?.type === "payment_confirmed" || row?.type === "payment_received") {
            const bookingId = row?.data?.booking_id;
            const seenKey = `pay_confirmed_shown:${bookingId}`;
            try {
              if (bookingId && sessionStorage.getItem(seenKey)) return;
              if (bookingId) sessionStorage.setItem(seenKey, "1");
            } catch {}
            setPayload({ booking_id: bookingId, property_title: row?.data?.property_title });
            setOpen(true);
          }
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  const handleCallTaxi = () => {
    // Fecha o modal de confirmação
    setOpen(false);
    // Se existir deep link nativo, abre directamente sem modal intermédio
    if (taxiDeepLink) {
      try {
        window.location.href = taxiDeepLink;
      } catch {}
      return;
    }
    // Caso contrário mostra modal com link do parceiro
    if (taxiLink) setTaxiOpen(true);
  };

  const goToBooking = () => {
    setOpen(false);
    if (payload?.booking_id) navigate(`/reservas/${payload.booking_id}`);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl max-w-md">
          <DialogHeader>
            <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mb-2">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <DialogTitle className="text-center">{tAuto("payment.confirmedTitle", "Pagamento confirmado")}</DialogTitle>
            <DialogDescription className="text-center">
              A sua reserva {payload?.property_title ? `em ${payload.property_title}` : ""} foi confirmada com sucesso.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col gap-2 sm:flex-col">
            {(taxiLink || taxiDeepLink) && (
              <Button onClick={handleCallTaxi} className="w-full bg-[#E8513D] hover:bg-[#D14430] text-white">
                <Car className="w-4 h-4 mr-2" /> Chamar Táxi
              </Button>
            )}
            <Button variant="outline" onClick={goToBooking} className="w-full">
              Ver reserva
            </Button>
            <Button variant="ghost" onClick={() => setOpen(false)} className="w-full">
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={taxiOpen} onOpenChange={setTaxiOpen}>
        <DialogContent className="rounded-2xl max-w-md">
          <DialogHeader>
            <DialogTitle>{tAuto("payment.taxiPartner", "Parceiro de Táxi")}</DialogTitle>
            <DialogDescription>
              Vamos abrir o parceiro de táxi numa nova janela. Confirme a viagem directamente na plataforma do parceiro.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col gap-2 sm:flex-col">
            <Button
              onClick={() => {
                if (taxiLink) window.open(taxiLink, "_blank", "noopener,noreferrer");
                setTaxiOpen(false);
              }}
              className="w-full bg-[#E8513D] hover:bg-[#D14430] text-white"
            >
              Abrir parceiro
            </Button>
            <Button variant="ghost" onClick={() => setTaxiOpen(false)} className="w-full">
              Cancelar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
